import React, { useEffect, useState } from 'react';
import { Box, Slider } from '@mui/material';
import { useTranslation } from 'react-i18next';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import { EMPHASIS_WARNING_THRESHOLD } from '@src/constants/request';
import CommonButton from '@src/new-components/Button';
import Typography from '@src/new-components/Typography';
import { COLOR, GRADIENT_COLOR } from '@src/styles/color';
import { StyledDrawer } from './index.style';

const EmphasisDrawer = ({ open, onClose, currentEmphasis, onChange }) => {
  const { t } = useTranslation();

  const [emphasis, setEmphasis] = useState(currentEmphasis);
  const [showWarning, setShowWarning] = useState(false);

  const handleCloseEmphasisDrawer = () => onClose();

  const handleShowWarning = (value) => {
    if (value > EMPHASIS_WARNING_THRESHOLD) {
      setShowWarning(true);
    } else {
      setShowWarning(false);
    }
  };

  const handleChangeEmphasis = (value) => {
    setEmphasis(value);
    handleShowWarning(value);
  };

  const handleApplyEmphasis = () => {
    onChange('emphasis', emphasis);
    onClose();
  };

  const handleResetEmphasis = () => {
    setEmphasis(currentEmphasis);
    handleShowWarning(currentEmphasis);
  };

  useEffect(() => {
    setEmphasis(currentEmphasis);
    handleShowWarning(currentEmphasis);
  }, [currentEmphasis]);

  return (
    <StyledDrawer open={open} onClose={onClose} anchor="bottom">
      <Box className="drawer-title">
        <Typography variant="body-16-bold" color={COLOR.darkGray}>
          {t('emphasisPopoverTitle')}
        </Typography>
        <CloseRoundedIcon
          sx={{ color: COLOR.navyBlue }}
          onClick={handleCloseEmphasisDrawer}
        />
      </Box>
      <Box className="drawer-content">
        <Typography variant="body-14-regular" style={{ color: COLOR.black }}>
          {t('emphasisPopoverDescription')}
        </Typography>
        <Box className="emphasis-slider">
          <Typography
            variant="body-12-regular"
            style={{ color: COLOR.label2['64'] }}
          >
            {t('emphasisNote')}
          </Typography>
          <Box className="emphasis-slider-value">
            <Slider
              value={emphasis}
              onChange={(e) => handleChangeEmphasis(e.target.value)}
              step={10}
              sx={{
                height: 8,
                padding: 0,
                '& .MuiSlider-track': {
                  backgroundColor: `${COLOR.bluev2['80']}`,
                  height: 8,
                  color: `${COLOR.bluev2['80']}`,
                  border: 'none',
                },
                '& .MuiSlider-rail': {
                  backgroundColor: `${COLOR.backgroundPending}`,
                  height: 8,
                },
                '& .MuiSlider-thumb': {
                  color: '#fff',
                  background: '#fff',
                  zIndex: 200,
                  boxShadow: '0px 3px 8px 0px #0000003D',
                },
                '& .MuiSlider-thumb:before': {
                  width: 16,
                  height: 16,
                  zIndex: 201,
                  background: `${GRADIENT_COLOR.blueGradient}`,
                  boxShadow: `0px 2px 8px 0px ${COLOR.bluev2['48']}`,
                },
                '& .MuiSlider-mark': {
                  backgroundColor: 'transparent',
                },
                '& .MuiSlider-thumb.Mui-focusVisible': {
                  boxShadow: 'none',
                },
              }}
            />
            <Typography
              variant="body-12-regular"
              style={{ color: COLOR.label2['64'] }}
            >
              {emphasis}%
            </Typography>
          </Box>
        </Box>
        {showWarning && (
          <Typography
            variant="body-12-regular"
            style={{ color: COLOR.orangeV2['100'] }}
          >
            {t('emphasisWarning')}
          </Typography>
        )}
      </Box>
      <Box className="drawer-action">
        <CommonButton
          variant="outlined"
          textProps={{
            variantText: 'body-14-semibold',
            color: COLOR.darkBlue,
          }}
          customStyle={{
            padding: '8px 24px',
            height: '40px',
          }}
          onClick={handleResetEmphasis}
        >
          {t('reset')}
        </CommonButton>
        <CommonButton
          variant="contained"
          textProps={{
            variantText: 'body-14-semibold',
            color: COLOR.darkBlue,
          }}
          customStyle={{
            padding: '8px 24px',
            height: '40px',
            flex: 1,
          }}
          onClick={handleApplyEmphasis}
        >
          {t('apply')}
        </CommonButton>
      </Box>
    </StyledDrawer>
  );
};

export default EmphasisDrawer;
